import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  Observable,
  switchMap,
  map,
  catchError,
  of,
  from,
  combineLatest,
  take
} from 'rxjs';
import * as fromDoubtsActions from '../actions/doubts.actions';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import {
  AngularFirestore,
  Query,
  QueryFn
} from '@angular/fire/compat/firestore';
import {
  DoubtFirebaseResponse,
  Doubt,
  DoubtsFilter
} from 'src/app/models/doubt.model';
import { User } from 'src/app/models/user.model';

@Injectable()
export class DoubtsEffects {
  private readonly collectionName = 'doubts';
  constructor(
    private readonly action$: Actions,
    private readonly firebaseService: FirebaseService,
    private readonly firestore: AngularFirestore
  ) {}

  // Load Doubts
  loadDoubts$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromDoubtsActions.LoadDoubtsAction),
      switchMap(
        ({
          trainer,
          filters
        }: {
          type: string;
          trainer: User;
          filters?: DoubtsFilter;
        }) =>
          this.getDoubtsForTrainer(trainer, filters).pipe(
            switchMap((doubts) => this.addUsersToDoubts(doubts, trainer)),
            map((doubts) => ({
              type: fromDoubtsActions.LOAD_DOUBTS_SUCCESS,
              data: doubts
            })),
            catchError((error) =>
              of({ type: fromDoubtsActions.LOAD_DOUBTS_FAIL, error })
            )
          )
      )
    )
  );

  // Add Comment
  addComment$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromDoubtsActions.AddComentAction),
      switchMap(async ({ comment, doubt }) => {
        const doubtToEdit: Doubt = JSON.parse(
          JSON.stringify({
            comments: [...(doubt.comments || []), comment]
          })
        );
        doubtToEdit.comments?.forEach((comment) => {
          delete comment.user;
        });
        delete doubtToEdit.id;
        await this.firebaseService.updateDoc(
          this.collectionName,
          doubt.id!,
          doubtToEdit
        );
        return {
          comment: {
            ...comment,
            user: comment.userId === doubt.user?.id ? doubt.user : doubt.trainer
          },
          doubt
        };
      }),
      map(({ comment, doubt }) => {
        return {
          type: fromDoubtsActions.ADD_COMMENT_SUCCESS,
          comment,
          doubt
        };
      }),
      catchError((error) =>
        of({ type: fromDoubtsActions.ADD_COMMENT_FAIL, error: error })
      )
    )
  );

  private getDoubtsForTrainer(
    trainer: User,
    filters?: DoubtsFilter
  ): Observable<Doubt[]> {
    const queryFn: QueryFn = (ref) => {
      let query: Query = ref;
      if (filters?.state) {
        query = query.where('state', '==', filters?.state);
      }
      if (filters?.subject) {
        const start = filters.subject;
        const end = filters.subject + '\uf8ff'; // `'\uf8ff'` es el último carácter en Unicode para cubrir todos los valores posibles

        query = query.where('subject', '>=', start).where('subject', '<', end);
      }
      if (filters?.user) {
        const userRef = this.firestore.doc(`users/${filters.user}`).ref;
        query = query.where('userRef', '==', userRef);
      }
      const trainerRef = this.firestore.doc(`users/${trainer.id}`).ref;
      query = query.where('trainerRef', '==', trainerRef);

      return query;
    };

    return this.firebaseService
      .getDataOfCollectionByMultipleFields(this.collectionName, queryFn)
      .pipe(
        take(1),
        map((resp) =>
          resp.docs.map((doc) => ({
            ...(doc.data() as DoubtFirebaseResponse),
            id: doc.id
          }))
        )
      );
  }

  private addUsersToDoubts(
    doubts: Doubt[],
    trainer: User
  ): Observable<Doubt[]> {
    if (doubts?.length > 0) {
      const userObservables = doubts.map((doubt) => from(doubt.userRef!.get()));
      return combineLatest(userObservables).pipe(
        map((users) =>
          doubts.map((doubt) => {
            const user: User = {
              ...users
                .find((userDoc) => userDoc.id === doubt.userRef!.id)
                ?.data(),
              id: doubt.userRef!.id
            } as any;
            const comments = doubt.comments?.map((comment) => ({
              ...comment,
              user: user.id === comment.userId ? user : trainer,
              date: new Date(
                (comment.date as unknown as { seconds: number }).seconds
              )
            }));
            doubt.date = new Date(
              (doubt.date as unknown as { seconds: number }).seconds
            );
            delete doubt.trainerRef;
            delete doubt.userRef;
            delete user.trainerRef;
            delete user.teamRef;

            return {
              ...doubt,
              trainer,
              user,
              comments
            } as Doubt;
          })
        )
      );
    }
    return of(doubts);
  }
}
